import { useEffect, useState } from "react";
import { Apple, Copy, RefreshCw, CalendarPlus, Clock } from "lucide-react";
import { toast } from "sonner";
import { web, errMsg, calendarUrl } from "../../lib/api";
import { Btn, Loader, ErrorNote } from "./kit";
import { Modal } from "./Form";

const links = (https) => {
  const webcal = https.replace(/^https?:\/\//, "webcal://");
  return { https, webcal, google: `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}` };
};

const Pending = () => (
  <div data-testid="calendar-pending" className="flex items-start gap-3 rounded-xl border border-amber-400/40 bg-amber-400/10 p-4 text-sm text-amber-100">
    <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
    <span>Your link is reserved, but the calendar won't load events until the GAMEDAY mobile app adds its schedule connection. Nothing to do on your end — the same link starts working once that's live.</span>
  </div>
);

export const CalendarLink = ({ open, onClose, teamId, teamName, path = `/teams/${teamId}/calendar-link`, urlFor = calendarUrl, title = "Team calendar",
  description = `Players and parents add ${teamName ?? "your team"}'s schedule to their phone calendar. New and changed events show up automatically.`,
  copied = "Link copied — share it with players and parents", resetNote = "Anyone using the old link stops getting updates until you send them the new one." }) => {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setState({ loading: true, error: null, data: null });
    web.post(path)
      .then((r) => { if (live) setState({ loading: false, error: null, data: r.data }); })
      .catch((e) => { if (live) setState({ loading: false, error: errMsg(e), data: null }); });
    return () => { live = false; };
  }, [open, path]);

  const reset = async () => {
    if (!window.confirm(`Reset the calendar link? ${resetNote}`)) return;
    setBusy(true);
    try {
      const { data } = await web.post(`${path}${path.includes("?") ? "&" : "?"}reset=true`);
      setState({ loading: false, error: null, data });
      toast.success("New calendar link created");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(copied);
    } catch {
      toast.error("Couldn't copy. Select the link and copy it manually.");
    }
  };

  const token = state.data?.token;
  const l = token ? links(urlFor(token)) : null;
  return (
    <Modal open={open} onClose={onClose} testid="calendar-link-dialog" title={title} description={description}>
      {state.loading ? <Loader /> : state.error ? <ErrorNote error={state.error} /> : l && (
        <div className="mt-2 space-y-5">
          {!state.data.ready && <Pending />}
          <div>
            <p className="mb-1.5 font-mono-gd text-[11px] uppercase tracking-[0.2em] text-slate-400">Your calendar link</p>
            <div className="flex items-center gap-2 rounded-xl border border-[#1E293B] bg-[#0F172A] p-1.5 pl-3.5">
              <input readOnly data-testid="calendar-link-input" value={l.https} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 bg-transparent font-mono-gd text-xs text-slate-200 outline-none" />
              <Btn data-testid="calendar-copy-button" onClick={() => copy(l.https)} className="px-4 py-2 text-xs"><Copy className="h-3.5 w-3.5" />Copy</Btn>
            </div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <a href={l.webcal} data-testid="calendar-apple-link" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1E293B] bg-[#162032] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-[#39FF14]/50"><Apple className="h-4 w-4" />iPhone / Mac</a>
            <a href={l.google} target="_blank" rel="noreferrer" data-testid="calendar-google-link" className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1E293B] bg-[#162032] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-[#39FF14]/50"><CalendarPlus className="h-4 w-4" />Google Calendar</a>
          </div>
          <p className="text-xs leading-relaxed text-slate-400">Outlook and other apps: choose "Subscribe from web" or "Add calendar from URL" and paste the link. Calendar apps check for updates every few hours.</p>
          <div className="flex justify-between border-t border-[#1E293B] pt-4">
            <Btn variant="danger" disabled={busy} data-testid="calendar-reset-button" onClick={reset} className="px-4 py-2 text-xs"><RefreshCw className="h-3.5 w-3.5" />Reset link</Btn>
            <Btn variant="ghost" onClick={onClose} data-testid="calendar-close-button" className="px-4 py-2 text-xs">Done</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
};
